'use client'

import { useMutation } from '@apollo/client'
import { Button, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { useWorkspaceSetup } from '../../contexts/setup-workspace-context'
import { SEND_VERIFICATION_CODE } from '../../data/mutations/workspace'
import { useSetupWorkspaceModal } from '../../stores/setup-workspace-modal-store'
import { VerificationMethodEnum } from '../../types/company'
import { SetupWorkspaceLayout } from './setup-workspace-layout'

const createEmailAddressFormSchema = (validEmailDomain: string) =>
  z.object({
    email: z
      .string()
      .email('Please enter a valid email address')
      .refine((value) => value.toLowerCase().endsWith(validEmailDomain.toLowerCase()), {
        message: `Your email must end with ${validEmailDomain}`
      })
  })

/**
 * This component is used to enter the email address of the company.
 */
export const EmailAddress = () => {
  const { setEmailVerificationMethod, setStep, verificationMethod, receiveCodeMethod } = useWorkspaceSetup()
  const { workspaceID } = useSetupWorkspaceModal()
  const validEmailDomain = `@${verificationMethod?.value}`

  const emailAddressFormSchema = createEmailAddressFormSchema(validEmailDomain)
  type EmailAddressFormValues = z.infer<typeof emailAddressFormSchema>

  // Form setup
  const form = useForm<EmailAddressFormValues>({
    defaultValues: {
      email: ''
    },
    resolver: zodResolver(emailAddressFormSchema)
  })

  // sending code mutation
  const [sendVerificationCode, { loading, error }] = useMutation(SEND_VERIFICATION_CODE, {
    onCompleted: () => {
      setStep(8) // Setting the step to 8 will trigger the next step in the setup process which is the VerificationOTP component.
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const onSubmit = async (values: EmailAddressFormValues) => {
    setEmailVerificationMethod({
      type: VerificationMethodEnum.CompanyEmail,
      value: values.email
    })

    const input = {
      method: receiveCodeMethod,
      type: VerificationMethodEnum.CompanyEmail,
      value: values.email
    }

    await sendVerificationCode({
      context: {
        headers: {
          'ws-id': workspaceID || ''
        }
      },
      variables: input
    })
  }

  return (
    <SetupWorkspaceLayout
      title={`Enter a "${validEmailDomain}" email`}
      footer={{
        label: 'Back',
        onClick: () => setStep(5) // Setting the step to 5 will trigger the previous step in the setup process which is the SelectVerificationMethod component.
      }}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="email-address-form ">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email address</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e) => {
                      field.onChange(e)
                    }}
                    disabled={loading}
                    className="w-full"
                    aria-label={`${validEmailDomain} input`}
                    initialFocus={true}
                  />
                </FormControl>
                <FormMessage />
                {!!error?.message && <FormMessage>{error?.message}</FormMessage>}
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" variant="default" loading={loading} data-testid="submit-button">
            Next
          </Button>
        </form>
      </Form>
    </SetupWorkspaceLayout>
  )
}
